import React, { createContext, useContext, useEffect, useMemo } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { content } from './content'
import { languages, defaultLanguage, languageCodes, pathFor } from './languages'

const STORAGE_KEY = 'nv-lang'
// Search engines and link-preview bots shouldn't be bounced to a different
// language by their Accept-Language/navigator.language – that can stop them
// indexing the other locales. Real visitors still get the redirect below.
const BOT_RE = /bot|crawl|spider|slurp|bingpreview|facebookexternalhit|whatsapp|telegram|embedly|quora|pinterest|outbrain|vkshare|monitor|lighthouse|headless/i

function readStorage() {
  try {
    return localStorage.getItem(STORAGE_KEY)
  } catch {
    return null
  }
}

function writeStorage(code) {
  try {
    localStorage.setItem(STORAGE_KEY, code)
  } catch {
    /* private mode / storage disabled – language switching still works, it just won't be remembered */
  }
}

function langFromPath(pathname) {
  const segment = pathname.split('/')[1]
  return languageCodes.includes(segment) ? segment : defaultLanguage
}

function guessBrowserLanguage() {
  const candidates = navigator.languages?.length ? navigator.languages : [navigator.language]
  return candidates
    .map((tag) => tag?.slice(0, 2).toLowerCase())
    .find((code) => languageCodes.includes(code))
}

const LanguageContext = createContext(null)

export const useI18n = () => {
  const ctx = useContext(LanguageContext)
  if (!ctx) throw new Error('useI18n must be used within LanguageProvider')
  return ctx
}

export function LanguageProvider({ children }) {
  const location = useLocation()
  const navigate = useNavigate()
  const lang = langFromPath(location.pathname)
  const t = content[lang]

  // On a first, un-saved visit to the default URL ("/"), suggest a language
  // from the browser instead of always forcing German. A saved choice (from
  // a previous visit, or from switching languages) always wins, and bots are
  // left alone so every language stays crawlable at its own URL.
  useEffect(() => {
    if (location.pathname !== '/') return
    const saved = readStorage()
    if (saved && languageCodes.includes(saved)) {
      if (saved !== defaultLanguage) navigate(pathFor(saved) + location.hash, { replace: true })
      return
    }
    if (typeof navigator === 'undefined' || BOT_RE.test(navigator.userAgent)) return
    const guess = guessBrowserLanguage()
    if (guess && guess !== defaultLanguage) navigate(pathFor(guess) + location.hash, { replace: true })
    // Only ever run this on the very first render of "/" in this session.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Visiting a language directly (e.g. a bookmarked /bg link) counts as a
  // preference too, so a later visit to "/" remembers it.
  useEffect(() => {
    if (lang !== defaultLanguage) writeStorage(lang)
  }, [lang])

  useEffect(() => {
    document.documentElement.lang = lang
  }, [lang])

  const setLang = (code) => {
    writeStorage(code)
    navigate(pathFor(code) + location.hash)
  }

  const value = useMemo(
    () => ({
      lang,
      t,
      languages,
      setLang,
      switchHref: (code) => pathFor(code) + location.hash,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [lang, t, location.hash],
  )

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>
}
